import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconWarn } from '../components/auth/AuthBits';
import { submitContact } from '../api';
import Captcha, { CAPTCHA_ENABLED, CAPTCHA_PROMPT } from '../components/Captcha';
import '../styles/page-contact.css';
import Seo from '../components/Seo';

/* The /api/v1/contact 'contact_us' shape is validated field-by-field on the
   server: inquiryType, fullName (≥2), a valid email, companyName (≥2),
   subject (≥3) and message (≥10) are all required, plus a reCAPTCHA token and
   an empty `website` honeypot. Every one of those is collected below. */
const INQUIRY_TYPES = [
  'General Inquiry',
  'Sales & Pricing',
  'Product Demo',
  'Technical Support',
  'Partnership',
  'Security & Compliance',
];

const EMPLOYEE_SIZES = ['1–20', '21–100', '101–500', '500+'];

/* Known API statuses get a human sentence; anything else falls back to the
   message the server (or the network-error wrapper) already produced. */
function formError(e) {
  const s = e?.status;
  if (s === 401) return 'We couldn’t authorise that request. Try again, or email hello@teamgrid.ai.';
  if (s === 404) return 'We couldn’t reach the contact service. Email hello@teamgrid.ai and we’ll pick it up.';
  if (s === 409) return 'We already have a message from this email — we’ll reply shortly.';
  if (s === 429) return 'Too many messages just now. Please wait a minute and try again.';
  return e?.message || 'Something went wrong. Please try again.';
}

export default function ContactPage() {
  const navigate = useNavigate();

  const [v, setV] = useState({
    name: '',
    email: '',
    company: '',
    inquiryType: INQUIRY_TYPES[0],
    employeeSize: '',
    phone: '',
    subject: '',
    message: '',
    website: '', // honeypot — real people never fill this
  });
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const captchaRef = useRef(null);

  const set = (name) => (e) => {
    const val = e.target.value;
    setV((s) => ({ ...s, [name]: val }));
    if (err[name]) setErr((s) => ({ ...s, [name]: '' }));
  };

  /* Mirrors the server's validateContactUsRequest so people see the problem
     next to the field instead of a single lumped-together 400. */
  const check = () => {
    const next = {};
    if (v.name.trim().length < 2) next.name = 'Enter your full name (at least 2 characters).';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim())) next.email = 'Enter a valid work email.';
    if (v.company.trim().length < 2) next.company = 'Enter your company name (at least 2 characters).';
    if (v.subject.trim().length < 3) next.subject = 'Add a short subject (at least 3 characters).';
    if (v.message.trim().length < 10) next.message = 'Tell us a little more — at least 10 characters.';
    return next;
  };

  const submit = (e) => {
    e.preventDefault();
    if (busy) return;
    const next = check();
    setErr(next);
    const bad = Object.keys(next)[0];
    if (bad) {
      document.getElementById(`ct-${bad}`)?.focus();
      return;
    }
    if (CAPTCHA_ENABLED && !captcha) {
      setErr({ form: CAPTCHA_PROMPT });
      return;
    }
    setBusy(true);
    setErr({});
    submitContact({
      fullName: v.name,
      email: v.email,
      companyName: v.company,
      inquiryType: v.inquiryType,
      employeeSize: v.employeeSize,
      phone: v.phone,
      subject: v.subject,
      message: v.message,
      recaptchaToken: captcha,
      website: v.website,
    })
      .then(() => navigate('/thanks-contact-us'))
      .catch((e2) => setErr({ form: formError(e2) }))
      .finally(() => {
        setBusy(false);
        try { captchaRef.current?.reset(); } catch { /* not mounted */ }
        setCaptcha(null);
      });
  };

  const fieldErr = (k) =>
    err[k] ? <p style={{ fontSize: '12px', color: '#E0483D', fontWeight: 600, marginTop: '6px' }}>{err[k]}</p> : null;

  return (
    <>
      <Seo title="Contact Us | TeamGrid™" description="Questions about patterns, privacy, pricing or a rollout across 10,000 machines? Email hello@teamgrid.ai or message us and get a reply within a day." />
<header className="f-hero">
  <div className="container">
    <div className="f-grid">
      <div>
        <span className="hero-badge"><img className="mark" src="/assets/mark-teamgrid.svg" alt="" />We reply fast — it’s our thing <span className="arr">›</span></span>
        <h1 style={{fontSize: '52px', lineHeight: '57px', letterSpacing: '-2.1px', fontWeight: '700', color: 'var(--ink-hero)', margin: '20px 0 14px'}}>Talk to<br /><span style={{color: '#a9aab3'}}>a human</span></h1>
        <p style={{fontSize: '15.5px', lineHeight: '24px', color: 'var(--ink-soft)'}}>Questions about patterns, privacy, pricing or a rollout across 10,000 machines — we read everything and reply within a day.</p>
        <div className="cways">
          <div className="cway"><span className="ci" style={{background: '#e6f5f3'}}><svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24"><rect x="3.5" y="5.5" width="17" height="13" rx="2.5" fill="none" stroke="#007A6E" strokeWidth="2.2" /><path d="M4.5 8l7.5 5 7.5-5" stroke="#007A6E" strokeWidth="2.2" fill="none" /></svg></span><span><div className="ct">hello@teamgrid.ai</div><div className="cd">Sales, partnerships & everything else</div></span></div>
          <div className="cway"><span className="ci" style={{background: '#eaf2fb'}}><svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24"><path d="M4 6a2.5 2.5 0 012.5-2.5h11A2.5 2.5 0 0120 6v8a2.5 2.5 0 01-2.5 2.5H10l-4.5 4v-4H6.5A2.5 2.5 0 014 14z" fill="#366EB6" /></svg></span><span><div className="ct">24/7 in-app support</div><div className="cd">Median first reply: 4 minutes</div></span></div>
          <div className="cway"><span className="ci" style={{background: '#fdf1e8'}}><svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="15" rx="2.5" fill="none" stroke="#F2673A" strokeWidth="2.2" /><path d="M4 10h16M9 4v4M15 4v4" stroke="#F2673A" strokeWidth="2.2" /></svg></span><span><div className="ct">Prefer a call?</div><div className="cd"><a href="/book-demo" style={{color: 'var(--teal-deep)', fontWeight: '700'}}>Book a 20-minute demo →</a></div></span></div>
        </div>
      </div>
      <div className="fcard2">
        <form onSubmit={submit} noValidate>
          <h3 style={{fontSize: '19px', fontWeight: '800', color: 'var(--ink-strong)'}}>Send us a message</h3>

          {/* Short fields pair up: eight stacked rows made the card taller than
              the copy beside it and pushed the send button below the fold. */}
          <div className="fpair">
            <div className="fld">
              <label htmlFor="ct-inquiryType">What&apos;s this about?</label>
              <select id="ct-inquiryType" name="inquiryType" value={v.inquiryType} onChange={set('inquiryType')}>
                {INQUIRY_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="fld">
              <label htmlFor="ct-employeeSize">Team size <span className="opt">optional</span></label>
              <select id="ct-employeeSize" name="employeeSize" value={v.employeeSize} onChange={set('employeeSize')}>
                <option value="">Prefer not to say</option>
                {EMPLOYEE_SIZES.map((sz) => <option key={sz}>{sz}</option>)}
              </select>
            </div>
          </div>

          <div className="fpair">
            <div className="fld">
              <label htmlFor="ct-name">Your name</label>
              <input id="ct-name" name="name" autoComplete="name" placeholder="Ananya Sharma" value={v.name} onChange={set('name')} aria-invalid={!!err.name} />
              {fieldErr('name')}
            </div>
            <div className="fld">
              <label htmlFor="ct-email">Work email</label>
              <input id="ct-email" name="email" type="email" autoComplete="email" placeholder="you@company.com" value={v.email} onChange={set('email')} aria-invalid={!!err.email} />
              {fieldErr('email')}
            </div>
          </div>

          <div className="fpair">
            <div className="fld">
              <label htmlFor="ct-company">Company</label>
              <input id="ct-company" name="company" autoComplete="organization" placeholder="Acme Logistics" value={v.company} onChange={set('company')} aria-invalid={!!err.company} />
              {fieldErr('company')}
            </div>
            <div className="fld">
              <label htmlFor="ct-phone">Phone <span className="opt">optional</span></label>
              <input id="ct-phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="+91 98765 43210" value={v.phone} onChange={set('phone')} />
            </div>
          </div>

          <label htmlFor="ct-subject">Subject</label>
          <input id="ct-subject" name="subject" placeholder="Rollout across 400 machines" value={v.subject} onChange={set('subject')} aria-invalid={!!err.subject} />
          {fieldErr('subject')}

          <label htmlFor="ct-message">What would you like to know?</label>
          <textarea id="ct-message" name="message" rows="3" placeholder="Tell us about your team, your tools, your questions…" value={v.message} onChange={set('message')} aria-invalid={!!err.message}></textarea>
          {fieldErr('message')}

          {/* Honeypot: hidden from people and from screen readers, irresistible
              to bots. The server rejects the submission if it arrives filled. */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={v.website}
            onChange={set('website')}
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
          />

          <Captcha ref={captchaRef} onChange={setCaptcha} />

          {err.form && <p className="ax-err" role="alert" style={{ marginTop: '16px' }}><IconWarn />{err.form}</p>}
          <button className="btn-primary" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send message'}</button>
          <p className="fnote">We reply within one business day. No spam, ever.</p>
        </form>
        <div className="oksplash"><div className="okring"><svg aria-hidden="true" width="28" height="28" viewBox="0 0 16 16"><path fill="none" stroke="#1d7a43" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg></div><h3 style={{fontSize: '20px', fontWeight: '800', color: 'var(--ink-strong)'}}>Got it — we’ll be in touch within a day.</h3><p style={{fontSize: '13.5px', color: 'var(--ink-soft)', marginTop: '8px'}}>Meanwhile, your trial is one click away.</p><a className="btn-primary" href="/register" style={{marginTop: '18px'}}>Start free trial</a></div>
      </div>
    </div>
  </div>
</header>
    </>
  );
}
